import { useState } from "react";

import { cn, cva, platformProps } from "../../lib";
import { webOnly } from "../../lib/web-role";
import { Box } from "../../primitives";
import {
  AvatarFallbackPrimitive,
  AvatarImagePrimitive,
  AvatarRootPrimitive,
} from "../../primitives/avatar";
import { Icon } from "../icons";
import { Typography } from "../typography";
import {
  avatarBackgroundClass,
  avatarIconColorClass,
  avatarIconSize,
  avatarIndicatorSizeClass,
  avatarInitialsClass,
  avatarInitialsColor,
  avatarInitialsVariant,
  generateInitials,
} from "./lib";
import type { AvatarProps, AvatarVariant } from "./types";

// Each size binds Figma's avatar variable (the atom's xs is `avatar-md`), one
// step up on desktop through its `-dt` class. The layout is explicit (ADR-024):
// cva classes are out of the `luce/box-*` lint rules' reach.
const avatarVariants = cva(
  "rounded-radius-full relative flex flex-col items-center justify-center",
  {
    variants: {
      size: {
        xs: "w-avatar-md h-avatar-md md:w-avatar-md-dt md:h-avatar-md-dt",
        sm: "w-avatar-lg h-avatar-lg md:w-avatar-lg-dt md:h-avatar-lg-dt",
        md: "w-avatar-xl h-avatar-xl md:w-avatar-xl-dt md:h-avatar-xl-dt",
        lg: "w-avatar-2xl h-avatar-2xl md:w-avatar-2xl-dt md:h-avatar-2xl-dt",
        xl: "w-avatar-3xl h-avatar-3xl md:w-avatar-3xl-dt md:h-avatar-3xl-dt",
      },
      variant: avatarBackgroundClass,
    },
    defaultVariants: {
      size: "md",
      variant: "image",
    },
  },
);

function isIconVariant(v: AvatarVariant) {
  return v === "primary" || v === "primaryInverted" || v === "anonymous";
}

function isInitialsVariant(v: AvatarVariant) {
  return v === "initial" || v === "initialGrey" || v === "initialInverted";
}

export const Avatar = ({
  src,
  name,
  initials: customInitials,
  size = "md",
  variant = "image",
  indicator,
  fallbackVariant = "anonymous",
  className,
  testID,
}: AvatarProps) => {
  // The photo that failed. Any change of `src` loads again, including back to
  // one that failed before; reset during render, so there's no frame of the
  // old error.
  const [failedSrc, setFailedSrc] = useState<string>();
  const [shownSrc, setShownSrc] = useState(src);
  if (src !== shownSrc) {
    setShownSrc(src);
    setFailedSrc(undefined);
  }
  const hasError = !!src && failedSrc === src;

  const displayInitials = customInitials?.substring(0, 2).toUpperCase() || generateInitials(name);
  // With no name or initials, an initials look shows the anonymous placeholder (DS-017)
  const withInitials = <T extends AvatarVariant>(look: T) =>
    isInitialsVariant(look) && !displayInitials ? "anonymous" : look;
  const fallbackLook = withInitials(fallbackVariant);
  const effectiveVariant =
    variant === "image" && (!src || hasError) ? fallbackLook : withInitials(variant);
  const showsImage = effectiveVariant === "image" && !!src && !hasError;

  // A blank name is no name, as for the initials
  const trimmedName = name?.trim();
  let accessibilityLabel = trimmedName ? `${trimmedName}'s avatar` : "Avatar";
  if (indicator) {
    accessibilityLabel += ", status indicator";
  }

  const rootClassName = cn(avatarVariants({ size, variant: effectiveVariant }), className);

  const content = (
    <>
      {showsImage && (
        <AvatarRootPrimitive
          // Native passes `alt` to the photo, which then becomes an accessibility
          // element of its own (on Android a second stop): the circle has the name
          alt={webOnly(trimmedName || "Avatar")}
          className="rounded-radius-full size-full overflow-hidden"
        >
          <AvatarImagePrimitive
            src={src!}
            alt={trimmedName || "Avatar"}
            className="aspect-square size-full object-cover"
            onLoadingStatusChange={(status) => {
              if (status === "error") setFailedSrc(src);
            }}
          />
          <AvatarFallbackPrimitive
            className={cn(
              "flex size-full items-center justify-center",
              avatarBackgroundClass[fallbackLook],
            )}
          >
            {isIconVariant(fallbackLook) && (
              <Icon
                name="User"
                weight="fill"
                size={avatarIconSize[size]}
                className={avatarIconColorClass[fallbackLook]}
              />
            )}
            {isInitialsVariant(fallbackLook) && (
              <Typography
                variant={avatarInitialsVariant[size]}
                color={avatarInitialsColor[fallbackLook]}
                align="center"
                whiteSpace="nowrap"
                selectable={false}
                // The initials fill a fixed circle, so they keep their size at
                // any phone text size; the avatar's name already says who it is
                maxFontSizeMultiplier={1}
                className={avatarInitialsClass[size]}
              >
                {displayInitials}
              </Typography>
            )}
          </AvatarFallbackPrimitive>
        </AvatarRootPrimitive>
      )}

      {isIconVariant(effectiveVariant) && (
        <Icon
          name="User"
          weight="fill"
          size={avatarIconSize[size]}
          className={avatarIconColorClass[effectiveVariant]}
        />
      )}

      {isInitialsVariant(effectiveVariant) && (
        <Typography
          variant={avatarInitialsVariant[size]}
          color={avatarInitialsColor[effectiveVariant]}
          align="center"
          whiteSpace="nowrap"
          selectable={false}
          maxFontSizeMultiplier={1}
          className={avatarInitialsClass[size]}
        >
          {displayInitials}
        </Typography>
      )}

      {indicator && (
        <Box
          className={cn(
            "border-luce-neutral-background-default bg-luce-system-danger-default rounded-radius-full absolute right-0 top-0 z-10 border-2",
            avatarIndicatorSizeClass[size],
          )}
        />
      )}
    </>
  );

  return (
    <Box
      {...platformProps({ testID, slot: "avatar" })}
      accessibilityRole="image"
      accessibilityLabel={accessibilityLabel}
      // On native the avatar is one element read by its name, in place of the
      // photo's alt or the initials inside it (as Tag since #1814)
      accessible
      className={rootClassName}
    >
      {content}
    </Box>
  );
};

Avatar.displayName = "Avatar";
